/**
 * 물품 대여 — 날짜 · 재고 계산 (services/bt-service/src/utils/rentalLogic.ts 의 화면 쪽 사본 — 규칙을 바꾸면 둘 다 고친다)
 *  - 날짜는 모두 한국 날짜 문자열 'YYYY-MM-DD'
 *  - 영업일 = 토요일 · 주일(일요일) · 등록된 공휴일/휴무일이 아닌 날
 *  - 반납일 기본값 = 수령일 다음 날부터 세어 영업일 N일째
 */
export type RentalStatus = '입금대기' | '대여확정' | '대여중' | '반납완료' | '확인필요' | '반려' | '취소';
export const ACTIVE_STATUSES: RentalStatus[] = ['입금대기', '대여확정', '대여중'];

export type Holiday = { date: string; name: string };
export type OccLine = { itemId: string; qty: number; outQty?: number | null };
export type OccReservation = { _id?: any; id?: string; status: RentalStatus; start: string; end: string; lines: OccLine[] };
export type StockItem = { _id?: any; id?: string; total: number; repair?: number };

const pad = (n: number) => (n < 10 ? '0' : '') + n;
export const isYmd = (s: any): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
const parse = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const fmt = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
export const addDays = (s: string, n: number) => { const d = parse(s); d.setUTCDate(d.getUTCDate() + n); return fmt(d); };
export const diffDays = (a: string, b: string) => Math.round((parse(b).getTime() - parse(a).getTime()) / 864e5);
export const weekday = (s: string) => parse(s).getUTCDay();

export const offReason = (s: string, holidays: Holiday[] = []): string | null => {
  const w = weekday(s);
  if (w === 0) return '주일';
  if (w === 6) return '토요일';
  const h = holidays.find((x) => x.date === s);
  return h ? h.name || '공휴일' : null;
};
export const isOff = (s: string, holidays: Holiday[] = []) => !!offReason(s, holidays);
export const addBizDays = (s: string, n: number, holidays: Holiday[] = []) => {
  let d = s;
  let left = n;
  let guard = 0;
  while (left > 0 && guard++ < 400) { d = addDays(d, 1); if (!isOff(d, holidays)) left -= 1; }
  return d;
};

const idOf = (x: { _id?: any; id?: string }) => String(x.id ?? x._id ?? '');

/** 이 예약이 그날 재고를 차지하는가 (대여 중인데 반납일이 지났으면 오늘까지 계속 차지) */
export const occupies = (r: OccReservation, day: string, today: string) => {
  if (!ACTIVE_STATUSES.includes(r.status)) return false;
  const end = r.status === '대여중' && today > r.end ? today : r.end;
  return day >= r.start && day <= end;
};

export const remainingOn = (item: StockItem, day: string, reservations: OccReservation[], today: string, excludeId?: string) => {
  const itemId = idOf(item);
  let n = (item.total || 0) - (item.repair || 0);
  reservations.forEach((r) => {
    if ((excludeId && idOf(r) === excludeId) || !occupies(r, day, today)) return;
    r.lines.forEach((l) => {
      if (String(l.itemId) !== itemId) return;
      n -= r.status === '대여중' && l.outQty != null ? l.outQty : l.qty;
    });
  });
  return n;
};

export const minRemaining = (item: StockItem, from: string, to: string, reservations: OccReservation[], today: string, excludeId?: string) => {
  if (!isYmd(from) || !isYmd(to) || to < from) return remainingOn(item, isYmd(from) ? from : today, reservations, today, excludeId);
  let min = Infinity;
  for (let d = from, i = 0; d <= to && i < 400; d = addDays(d, 1), i += 1) min = Math.min(min, remainingOn(item, d, reservations, today, excludeId));
  return min;
};

export const overdueDays = (r: { status: RentalStatus; end: string }, today: string) =>
  (r.status === '대여중' && today > r.end ? diffDays(r.end, today) : 0);

/** 교회 화면에 보일 교회 이름: 첫 글자 + ○○ (+ 교회) */
export const maskChurch = (name = '') => `${name.slice(0, 1)}○○${/교회$/.test(name) ? '교회' : ''}`;

/** 받침에 따라 조사 고르기: josa('퀴즈마스터', '은', '는') → '퀴즈마스터는' */
export const josa = (word: string, withBatchim: string, without: string) => {
  const ch = (word || '').trim().slice(-1);
  const code = ch.charCodeAt(0) - 0xac00;
  const has = code >= 0 && code <= 11171 ? code % 28 !== 0 : /[0-9lmnLMN]$/.test(ch) && !/[2459]$/.test(ch);
  return `${word}${has ? withBatchim : without}`;
};
